import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { WhatsappTemplatesService } from 'src/app/parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { ChooseTemplateFlowService } from 'src/app/shared/components/choose-template-flow/shared/choose-template-flow.service';
import { Favorite } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { FavoritesService } from 'src/app/shared/services/favorites.service';
import { SuggestAnswerService } from 'src/app/shared/services/suggest-answer.service';
import { UploadService } from 'src/app/shared/services/upload.service';
import { ModalDismissRoleEnum } from 'src/app/task-process/answer-editor/send-and-popover/modal-dismiss-role.enum';
import { environment } from 'src/environments/environment';

import { SingleMessages } from '../../shared/components/single-message-editor/single-messages';
import { AfterSendAction, AfterSendActionType, AnswersAndAfterSendAction } from '../shared/model';
import { WhatsappTemplate } from '../shared/models';
import { TaskProcessContext } from '../shared/task-process-context.class';
import { SendAndPopoverComponent } from './send-and-popover/send-and-popover.component';
import { SendAndTriggerSurveyComponent } from './send-and-popover/send-and-trigger-survey/send-and-trigger-survey.component';

@Component({
  selector: 'app-answer-editor',
  templateUrl: './answer-editor.component.html',
  styleUrls: ['./answer-editor.component.scss'],
})
export class AnswerEditorComponent implements OnInit, OnDestroy {
  @Input() taskProcessContext: TaskProcessContext;
  @Output() sendAnswer = new EventEmitter<AnswersAndAfterSendAction>();

  isWhatsappTemplateMode: boolean;
  whatsappTemplates: WhatsappTemplate[];
  singleMessages: SingleMessages;
  isExpanded = false;
  textareaPlaceholder = ' '; // init to space character to work around weird bug with mobile Safari
  // whereby the placeholder isn't displayed when first set by angular
  // (even though the dev tools do show the attribute in the DOM tree)
  isTextAreaBluePlaceholder = false;

  isSendButtonDisabled = true;

  private subscriptions = new Subscription();
  private favoritesFromBackend: Favorite[] = [];
  filteredFavorites: Favorite[] = [];
  filteredFavoritesAllLang: Favorite[] = [];

  constructor(
    private alcmeonContext: AlcmeonContextService,
    private basicService: BasicService,
    private chooseTemplateFlowService: ChooseTemplateFlowService,
    private favoritesService: FavoritesService,
    private suggestAnswerService: SuggestAnswerService,
    private uploadService: UploadService,
    private whatsappTemplatesService: WhatsappTemplatesService
  ) {
    this.singleMessages = new SingleMessages(this.basicService, this.uploadService, this.alcmeonContext);

    /*
     * Enhanced performances to avoid evaluating 3 times on each angular life cycle.
     */
    this.subscriptions.add(
      interval(300).subscribe(() => {
        this.checkSendButtonDisabled();
      })
    );
  }

  ngOnInit() {
    this.singleMessages.setSiteId(this.taskProcessContext.site.id);
    this.singleMessages.setEditorConfig(this.taskProcessContext.editorConfig);
    this.taskProcessContext.setSingleMessages(this.singleMessages);
    this.subscriptions.add(
      this.taskProcessContext.isProcessedTaskLoaded$
        .pipe(first(Boolean))
        .subscribe(() => this.initWhenTaskProcessContextLoaded())
    );

    if (!environment.isNativeApp && !this.taskProcessContext.engagement) {
      this.suggestAnswerService
        .getAissitantConfigForTeam(this.taskProcessContext.tagsContext.routingTagGroup.selectedTagId)
        .subscribe(resp => {
          this.suggestAnswerService.AissitantConfigForTeamBS.next(resp);
        });
    }
  }

  private initWhenTaskProcessContextLoaded() {
    const forceWhatsappTemplateMode =
      !!this.taskProcessContext.whatsappTemplates && this.whatsappTemplatesService.forceWhatsAppTemplateMode();
    this.isWhatsappTemplateMode = this.taskProcessContext.isWhatsappTemplateModeMandatory || forceWhatsappTemplateMode;
    this.initTextareaPlaceholder();
    this.singleMessages.addMessage();
    if (this.isWhatsappTemplateMode) {
      this.whatsappTemplates = this.taskProcessContext.whatsappTemplates || [];
    } else {
      this.initLoadFavorites();
    }
    if (environment.bundledWithAngularJSApp && this.taskProcessContext.isReplyingInPrivate()) {
      // we don't expand and focus the editor in public mode (aka inline editor),
      // as it seems that sometimes the browser tries to adjust the scroll to get the editor in focus,
      // which messes with our own scroll-to-message mechanism.
      this.expandEditor(false);
      if (!this.isWhatsappTemplateMode) {
        // couldn't find out why such a long delay is necessary before focusing the textarea,
        // (it looks like ionic is removing the focus at some stage of its init, maybe around when it sets .hydrated on components)
        setTimeout(() => this.singleMessages.lastMessage().doFocus(), 500);
      }
    }
  }

  private initLoadFavorites() {
    // Timeout to try and fix the occasional unknown network error
    setTimeout(() => this.loadFavorites(), 300);

    // Load the new favorites that match the filters each time a tag is changed
    this.subscriptions.add(
      this.taskProcessContext.tagsContext.tagsChange$.subscribe(() => this.updateFilteredFavorites())
    );
  }

  private initTextareaPlaceholder() {
    if (this.taskProcessContext.isReplyingInPrivate()) {
      this.textareaPlaceholder = this.basicService.translate('m/task-process/placeholder-private');
    } else {
      this.textareaPlaceholder = this.basicService.translate('m/task-process/placeholder-public');
    }
  }

  expandEditor(doFocus = true) {
    this.isExpanded = true;
    this.textareaPlaceholder = this.basicService.translate('m/task-process/placeholder-see-answer');
    this.isTextAreaBluePlaceholder = true;
    if (doFocus) {
      // give the browser a chance to display the textarea and then focus it so that the keyboard shows up
      this.focusAfterTimeout();
    }
  }

  focusAfterTimeout() {
    this.singleMessages.lastMessage().doFocusAfterTimeout();
  }

  foldEditor() {
    this.isExpanded = false;
  }

  /**
   * Called by the TPP in CRMMode after a sucessful answer
   * (as we don't go back to the task list in CRM mode)
   */
  clearEditors() {
    if (this.isWhatsappTemplateMode) {
      this.chooseTemplateFlowService.reset();
    } else {
      this.singleMessages.removeAllMessages();
      this.singleMessages.addMessage();
    }
  }

  private checkSendButtonDisabled() {
    const editorsValid = this.isWhatsappTemplateMode
      ? this.chooseTemplateFlowService.isValid
      : this.singleMessages.areAllMessagesValid;
    const tagsContext = this.taskProcessContext.tagsContext;
    const tagsInvalid = !tagsContext || tagsContext.hasMandatoryAndVisibleTagsMissing;
    this.isSendButtonDisabled = !editorsValid || tagsInvalid;
  }

  onDesktopSendClicked() {
    this.send({ type: AfterSendActionType.None });
  }

  async onMobileSendClicked() {
    if (this.taskProcessContext.shouldOfferToTriggerSurvey()) {
      await this.offerToTriggerSurveyThenSend();
    } else {
      this.send({ type: AfterSendActionType.None });
    }
  }

  async onDesktopSendAndClicked(event) {
    const componentProps = { taskProcessContext: this.taskProcessContext };
    const popoverResult = await this.basicService.popoverWithResult(event, SendAndPopoverComponent, componentProps);
    if (popoverResult.role === ModalDismissRoleEnum.OK) {
      this.send(popoverResult.data);
    }
  }

  private send(afterSendAction: AfterSendAction) {
    const answers = this.isWhatsappTemplateMode
      ? [this.chooseTemplateFlowService.selectedTemplateAsEditor2Answer()]
      : this.singleMessages.getEditor2Answer();
    this.sendAnswer.emit({ answers, afterSendAction });
  }

  private async offerToTriggerSurveyThenSend() {
    const modalResult = await this.basicService.popoverWithResult(null, SendAndTriggerSurveyComponent);
    if (modalResult.role === ModalDismissRoleEnum.OK) {
      const actionType = modalResult.data ? AfterSendActionType.TriggerSurvey : AfterSendActionType.None;
      this.send({ type: actionType });
    }
  }

  private loadFavorites() {
    this.favoritesService.getFavorites().subscribe(response => {
      this.favoritesFromBackend = response.favorites;
      this.updateFilteredFavorites();
    });
  }

  private updateFilteredFavorites() {
    if (this.favoritesFromBackend?.length) {
      const tagsContext = this.taskProcessContext.tagsContext;
      const site: any = this.taskProcessContext.site;
      const taskTypeInt = this.taskProcessContext.processedTaskInfo.taskTypeInt;
      const routingTagGroup = tagsContext.allTagGroups.find(tg => tg.isRouting);
      const nonRoutingTagGroups = tagsContext.allTagGroups.filter(tg => !tg.isRouting);

      this.filteredFavorites = this.favoritesService.processAndFilterFavorites(
        this.favoritesFromBackend,
        site,
        taskTypeInt,
        routingTagGroup,
        nonRoutingTagGroups,
        false
      );

      this.filteredFavoritesAllLang = this.favoritesService.processAndFilterFavorites(
        this.favoritesFromBackend,
        site,
        taskTypeInt,
        routingTagGroup,
        nonRoutingTagGroups,
        true
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
    this.chooseTemplateFlowService.reset();
  }
}
